import React, {Dispatch, SetStateAction, useState} from 'react';
import {StyleProp, StyleSheet, TextStyle, View, ViewStyle} from 'react-native';
import DropDownPicker, {
  DropDownPickerProps as RNDropDownPickerProps,
  ItemType,
} from 'react-native-dropdown-picker';
import moment from 'moment-timezone';

import {fontFamilyStyle} from './Text';

const months = [
  {label: 'January', value: '01'},
  {label: 'February', value: '02'},
  {label: 'March', value: '03'},
  {label: 'April', value: '04'},
  {label: 'May', value: '05'},
  {label: 'June', value: '06'},
  {label: 'July', value: '07'},
  {label: 'August', value: '08'},
  {label: 'September', value: '09'},
  {label: 'October', value: '10'},
  {label: 'November', value: '11'},
  {label: 'December', value: '12'},
];

const days = Array.from({length: 31}, (_, i) => ({
  label: `${i + 1}`,
  value: `${i + 1}`.padStart(2, '0'),
}));
const currentYear = new Date().getFullYear() - 18; // 18: years old minimum
// 82: 100 - 18, 100: years old maximum
const years = Array.from({length: 82}, (_, i) => ({
  label: `${currentYear - i}`,
  value: `${currentYear - i}`,
}));

const styles = StyleSheet.create({
  main: {
    minHeight: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dayYearDropdownContainer: {
    width: '28%',
  },
  monthDropdownContainer: {
    width: '38%',
  },
  dropDown: {
    borderColor: 'rgb(134, 147, 158)',
    ...fontFamilyStyle,
  },
  dropDownDisabled: {
    ...fontFamilyStyle,
    backgroundColor: 'rgb(243, 243, 243)',
    color: 'rgb(122, 122, 122)',
    borderColor: 'rgb(156, 163, 175)',
    opacity: 0.5,
  },
});

type DropDownPickerProps = RNDropDownPickerProps<string>;
type PartialDropDownPickerProps = Partial<DropDownPickerProps>;

const commonDropDownPickerProps: PartialDropDownPickerProps = {
  listMode: 'SCROLLVIEW',
  multiple: false,
  textStyle: {
    ...fontFamilyStyle,
  },
};

const getInitialDateOfBirthValues = (date?: Date) => {
  if (!date)
    return {
      day: null,
      month: null,
      year: null,
    };

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  return {day, month, year};
};

type Props = {
  style?: StyleProp<ViewStyle>;
  dropDownContainerStyle?: StyleProp<ViewStyle>;
  dropDownStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  onChange: (date: Date) => void;
  onInvalidDate: (date: Date) => void;
  initialDate?: Date;
  disabled?: boolean;
};
const DateOfBirthInput = ({
  onChange,
  onInvalidDate,
  style,
  dropDownContainerStyle,
  dropDownStyle,
  textStyle,
  initialDate,
  disabled,
}: Props) => {
  const [isDayDropdownOpen, setDayDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setYearDropdownOpen] = useState(false);

  const initialValues = getInitialDateOfBirthValues(initialDate);

  const [day, setDay] = useState<string | null>(initialValues.day);
  const [month, setMonth] = useState<string | null>(initialValues.month);
  const [year, setYear] = useState<string | null>(initialValues.year);

  // https://hossein-zare.github.io/react-native-dropdown-picker-website/docs/usage#single-item 🤷🏻‍♂️
  const [dayItems, setDayItems] = useState(days);
  const [monthItems, setMonthItems] = useState(months);
  const [yearItems, setYearItems] = useState(years);

  const handleDayOpen: Dispatch<SetStateAction<boolean>> = (
    value: SetStateAction<boolean>,
  ) => {
    setDayDropdownOpen(value);
    setMonthDropdownOpen(false);
    setYearDropdownOpen(false);
  };

  const handleMonthOpen: Dispatch<SetStateAction<boolean>> = (
    value: SetStateAction<boolean>,
  ) => {
    setMonthDropdownOpen(value);
    setDayDropdownOpen(false);
    setYearDropdownOpen(false);
  };

  const handleYearOpen: Dispatch<SetStateAction<boolean>> = (
    value: SetStateAction<boolean>,
  ) => {
    setYearDropdownOpen(value);
    setDayDropdownOpen(false);
    setMonthDropdownOpen(false);
  };

  const handleSelectDay = (item: ItemType<string>) =>
    handleSelectDate(year, month, item.value);
  const handleSelectMonth = (item: ItemType<string>) =>
    handleSelectDate(year, item.value, day);
  const handleSelectYear = (item: ItemType<string>) =>
    handleSelectDate(item.value, month, day);

  const handleSelectDate = (
    selectedYear: string | null | undefined,
    selectedMonth: string | null | undefined,
    selectedDay: string | null | undefined,
  ) => {
    if (!selectedYear || !selectedMonth || !selectedDay) return null;

    const momentDate = moment(
      `${selectedYear}-${selectedMonth}-${selectedDay}`,
      'YYYY-MM-DD',
    ).tz('Australia/Sydney');
    const date = momentDate.toDate();

    if (momentDate.isValid()) {
      onChange(date);
    } else {
      onInvalidDate(date);
    }
  };

  return (
    <View style={[styles.main, style]}>
      <DropDownPicker
        {...commonDropDownPickerProps}
        containerStyle={styles.dayYearDropdownContainer}
        style={[styles.dropDown, dropDownStyle]}
        textStyle={disabled ? styles.dropDownDisabled : [commonDropDownPickerProps.textStyle, textStyle]}
        dropDownContainerStyle={dropDownContainerStyle}
        listParentContainerStyle={{zIndex: 1}}
        items={dayItems}
        onSelectItem={handleSelectDay}
        open={isDayDropdownOpen}
        placeholder="Day"
        setItems={setDayItems}
        setOpen={handleDayOpen}
        setValue={setDay}
        value={day}
        disabled={disabled}
        disabledStyle={styles.dropDownDisabled}
      />

      <DropDownPicker
        {...commonDropDownPickerProps}
        containerStyle={styles.monthDropdownContainer}
        style={[styles.dropDown, dropDownStyle]}
        textStyle={disabled ? styles.dropDownDisabled : [commonDropDownPickerProps.textStyle, textStyle]}
        dropDownContainerStyle={dropDownContainerStyle}
        items={monthItems}
        onSelectItem={handleSelectMonth}
        open={isMonthDropdownOpen}
        placeholder="Month"
        setItems={setMonthItems}
        setOpen={handleMonthOpen}
        setValue={setMonth}
        value={month}
        disabled={disabled}
        disabledStyle={styles.dropDownDisabled}
      />

      <DropDownPicker
        {...commonDropDownPickerProps}
        containerStyle={styles.dayYearDropdownContainer}
        style={[styles.dropDown, dropDownStyle]}
        textStyle={disabled ? styles.dropDownDisabled : [commonDropDownPickerProps.textStyle, textStyle]}
        dropDownContainerStyle={dropDownContainerStyle}
        items={yearItems}
        onSelectItem={handleSelectYear}
        open={isYearDropdownOpen}
        placeholder="Year"
        setItems={setYearItems}
        setOpen={handleYearOpen}
        setValue={setYear}
        value={year}
        disabled={disabled}
        disabledStyle={styles.dropDownDisabled}
      />
    </View>
  );
};

export default DateOfBirthInput;
